// Lucian UX/UI Prompt #007 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#007
import React, { useEffect, useRef, useState } from "react";
import { onPlayReadStr } from "@/helpers";
import "./LucianPlayAudio.css";

export interface LucianPlayAudioProps {
  text?: string;
  src?: string;
  variant?: "compact" | "full" | "bar";
  title?: string;
  subtitle?: string;
  className?: string;
  onPlayStateChange?: (isPlaying: boolean) => void;
}

const PLAY = [
  [
    [13, 8],
    [22.5, 13.6],
    [22.5, 26.4],
    [13, 32],
  ],
  [
    [22.5, 13.6],
    [32, 20],
    [32, 20],
    [22.5, 26.4],
  ],
];

const PAUSE = [
  [
    [11, 9],
    [17, 9],
    [17, 31],
    [11, 31],
  ],
  [
    [23, 9],
    [29, 9],
    [29, 31],
    [23, 31],
  ],
];

export const LucianPlayAudio: React.FC<LucianPlayAudioProps> = ({
  text,
  src,
  variant = "compact",
  title = "Audio Track",
  subtitle = "FongFluency Audio Player",
  className = "",
  onPlayStateChange,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const leftPolyRef = useRef<SVGPolygonElement>(null);
  const rightPolyRef = useRef<SVGPolygonElement>(null);
  const waveRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(src ? 0 : 10);
  const [waveBars, setWaveBars] = useState<Array<{ h: number; d: number }>>([]);

  const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
  const uniqueIdRef = useRef(`u007-g-${Math.random().toString(36).slice(2, 7)}`);

  // Generate wave bar heights
  useEffect(() => {
    const bars: Array<{ h: number; d: number }> = [];
    const count = variant === "full" ? 40 : variant === "bar" ? 34 : 20;
    for (let i = 0; i < count; i++) {
      const h = 25 + Math.abs(Math.sin(i * 0.9) * 48 + Math.sin(i * 0.37) * 26);
      bars.push({
        h: Math.min(100, Math.max(15, Math.round(h))),
        d: (i % 7) / 7,
      });
    }
    setWaveBars(bars);
  }, [variant]);

  // Handle Morph animation between Play and Pause shapes
  const morph = (to: number) => {
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (rm) {
      draw(to);
      return;
    }
    const from = to === 1 ? 0 : 1;
    const t0 = performance.now();
    const D = 400;

    let rafId: number;
    const f = (now: number) => {
      const x = Math.min(1, (now - t0) / D);
      const e = 1 + 2.2 * Math.pow(x - 1, 3) + 1.2 * Math.pow(x - 1, 2);
      const shape = from + (to - from) * e;
      draw(shape);
      if (x < 1) {
        rafId = requestAnimationFrame(f);
      }
    };
    rafId = requestAnimationFrame(f);
    return () => cancelAnimationFrame(rafId);
  };

  const draw = (t: number) => {
    const polys = [leftPolyRef.current, rightPolyRef.current];
    polys.forEach((poly, k) => {
      if (!poly) return;
      const pts = PLAY[k]
        .map((p, j) => {
          const q = PAUSE[k][j];
          const x = (p[0] + (q[0] - p[0]) * t).toFixed(2);
          const y = (p[1] + (q[1] - p[1]) * t).toFixed(2);
          return `${x},${y}`;
        })
        .join(" ");
      poly.setAttribute("points", pts);
    });
  };

  // Draw initial Play shape on mount
  useEffect(() => {
    draw(0);
  }, []);

  // Update Morph when playing state changes
  useEffect(() => {
    morph(isPlaying ? 1 : 0);
    onPlayStateChange?.(isPlaying);
  }, [isPlaying]);

  // Setup Audio element if src provided
  useEffect(() => {
    if (!src) return;
    const audio = new Audio(src);
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audioRef.current = null;
    };
  }, [src]);

  // Clean up SpeechSynthesis on unmount
  useEffect(() => {
    return () => {
      if (synth) {
        synth.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Play / Pause toggle
  const togglePlay = () => {
    if (src && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
      return;
    }

    if (text) {
      if (isPlaying) {
        if (synth?.speaking) synth.pause();
        setIsPlaying(false);
      } else {
        if (synth?.paused) {
          synth.resume();
          setIsPlaying(true);
        } else {
          setIsPlaying(true);
          onPlayReadStr(text, () => {
            setIsPlaying(false);
            setCurrentTime(0);
          });
        }
      }
      return;
    }

    // Fallback toggle for demo without audio/text
    setIsPlaying((prev) => !prev);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!el || duration <= 0) return;
    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = ratio * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const formatTime = (secs: number) => {
    const s = Math.floor(secs);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const progress = duration > 0 ? currentTime / duration : 0;

  if (variant === "compact") {
    return (
      <div ref={rootRef} className={`ux u007-compact ${isPlaying ? "is-play" : ""} ${className}`}>
        <div className="u007-compact-pulse" aria-hidden="true" />
        <button
          className="u007-compact-btn"
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Tạm dừng" : "Phát âm"}
          aria-pressed={isPlaying}
        >
          <svg viewBox="0 0 40 40" aria-hidden="true">
            <polygon ref={leftPolyRef} className="u007-l" />
            <polygon ref={rightPolyRef} className="u007-r" />
          </svg>
        </button>
      </div>
    );
  }

  if (variant === "bar") {
    return (
      <div
        ref={rootRef}
        className={`ux u007-bar ${isPlaying ? "is-play" : ""} ${className}`}
        style={{ "--p": progress.toFixed(4) } as React.CSSProperties}
      >
        <div className="u007-bar-btn-wrap">
          <div className="u007-compact-pulse" aria-hidden="true" />
          <button
            className="u007-compact-btn"
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Tạm dừng" : "Phát"}
            aria-pressed={isPlaying}
          >
            <svg viewBox="0 0 40 40" aria-hidden="true">
              <polygon ref={leftPolyRef} className="u007-l" />
              <polygon ref={rightPolyRef} className="u007-r" />
            </svg>
          </button>
        </div>

        {/* Waveform Bar */}
        <div
          ref={waveRef}
          className="u007-wave u007-bar-wave"
          role="slider"
          tabIndex={0}
          onClick={handleSeek}
          style={{ cursor: "pointer" }}
          aria-label="Playback position"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          aria-valuetext={`${Math.floor(currentTime)} seconds`}
        >
          {waveBars.map((bar, idx) => {
            const isOn = idx < Math.round(progress * waveBars.length);
            return (
              <i
                key={idx}
                className={isOn ? "on" : ""}
                style={
                  {
                    "--h": bar.h,
                    "--d": bar.d,
                  } as React.CSSProperties
                }
              />
            );
          })}
        </div>

        <div className="u007-bar-time">
          <span>{formatTime(currentTime)}</span> / <span>{formatTime(duration)}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`ux u007 ${isPlaying ? "is-play" : ""} ${className}`}
      style={{ "--p": progress.toFixed(4) } as React.CSSProperties}
    >
      <div className="u007-info">
        <b>{title}</b>
        <span>{subtitle}</span>
      </div>

      <div className="u007-disc">
        <svg className="u007-ring" viewBox="0 0 160 160" aria-hidden="true">
          <defs>
            <linearGradient id={uniqueIdRef.current} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="var(--ux-accent)" />
              <stop offset="1" stopColor="var(--ux-mint)" />
            </linearGradient>
          </defs>
          <circle className="u007-track" cx="80" cy="80" r="72" />
          <circle className="u007-arc" cx="80" cy="80" r="72" style={{ stroke: `url(#${uniqueIdRef.current})` }} />
        </svg>

        <span className="u007-pulse" aria-hidden="true" />

        <button
          className="u007-btn"
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
          aria-pressed={isPlaying}
        >
          <svg viewBox="0 0 40 40" aria-hidden="true">
            <polygon ref={leftPolyRef} className="u007-l" />
            <polygon ref={rightPolyRef} className="u007-r" />
          </svg>
        </button>
      </div>

      {/* Waveform */}
      <div
        ref={waveRef}
        className="u007-wave"
        role="slider"
        tabIndex={0}
        onClick={handleSeek}
        style={{ cursor: "pointer" }}
        aria-label="Playback position"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={currentTime}
        aria-valuetext={`${Math.floor(currentTime)} seconds`}
      >
        {waveBars.map((bar, idx) => {
          const isOn = idx < Math.round(progress * waveBars.length);
          return (
            <i
              key={idx}
              className={isOn ? "on" : ""}
              style={
                {
                  "--h": bar.h,
                  "--d": bar.d,
                } as React.CSSProperties
              }
            />
          );
        })}
      </div>

      <div className="u007-time">
        <b className="u007-now">{formatTime(currentTime)}</b>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default LucianPlayAudio;
